import { useMemo, useRef } from 'react';

import type { ExperimentClient, ExperimentUser, Variants } from '@amplitude/experiment-js-client';
import { getConfig } from '@config';
import globalWindow from '@shared/core/globals';

import type { RemoteFeatureFlagService } from './remoteFeatureFlags.types';
// Resolves per build target: the browser SDK in client builds (`.web.ts`), the Node SDK on the
// server (`.ts`). See initializeExperimentClient.ts for why this is a resolution split and not an
// `isServer` branch — a static require behind a runtime guard still ships to browsers (PRNT-4899).
import { initializeExperimentClient } from './initializeExperimentClient';
import { withAmplitudeExposureTracking } from './amplitudeExposureTracking';
import { logEvent, parseError } from './remoteFeatureFlags.logger';

const config = getConfig();

export function useAmplitudeExperiment(initialVariantsProp?: Variants): RemoteFeatureFlagService {
  const isServer = typeof window === 'undefined';

  // Server-side: Store variants in ref since server SDK returns variants directly from fetch()
  const serverVariantsRef = useRef<Variants>(initialVariantsProp || {});

  // Read and delete SSR variants from window global exactly once during initialization
  // Using useRef ensures this side effect only happens once, not on every useMemo execution
  //
  // IMPORTANT: This ref only stores INITIAL variants for SDK initialization via initialVariants.
  // After initialization, the Amplitude SDK maintains its own internal state that gets updated
  // by fetch(). So background requests (via fetchUser) DO update the data - getFeatureValue()
  // reads from the SDK's internal state, not this ref. This architecture eliminates flicker
  // (SSR variants) while still providing fresh flags (background fetch).
  const ssrVariantsFromWindow = useRef<Variants | undefined>(
    globalWindow.__INITIAL_AMPLITUDE_FLAGS__
      ? (() => {
          const variants = globalWindow.__INITIAL_AMPLITUDE_FLAGS__;
          delete globalWindow.__INITIAL_AMPLITUDE_FLAGS__;
          return variants;
        })()
      : undefined,
  );

  /**
   * SSR Hydration Strategy:
   *
   * Server-side:
   * - Variants passed as prop from req.amplitudeFeatureFlagVariants (fetched in prefetchData)
   * - Used as initialVariants to initialize SDK with SSR-fetched flags
   *
   * Client-side:
   * - Variants read from window.__INITIAL_AMPLITUDE_FLAGS__ (embedded in HTML during SSR)
   * - Prop is undefined during client hydration (not passed through app.ssr.tsx)
   * - Window global overrides prop if present
   * - Global is deleted after reading to prevent stale data on client-side navigation
   *
   * Both approaches use Amplitude SDK's initialVariants parameter to eliminate flicker.
   * See: https://www.docs.developers.amplitude.com/experiment/guides/server-side-rendering
   *
   * Resolved outside the SDK memo so the no-SDK fallback below can still serve these variants.
   */
  const ssrVariants: Variants | undefined = initialVariantsProp || ssrVariantsFromWindow.current;

  const experimentSdk = useMemo<ExperimentClient | null>(() => {
    try {
      const client: ExperimentClient = withAmplitudeExposureTracking(exposureTrackingProvider => {
        const sdkConfig = {
          serverUrl: config.amplitudeExperimentServer,
          flagsServerUrl: config.amplitudeExperimentFlagsServer,
          exposureTrackingProvider,
          automaticExposureTracking: false,
          fetchOnStart: true,
          pollOnStart: false,
          // Only include initialVariants when we have SSR data to prevent SDK errors
          // The SDK defaults initialVariants to {} and accesses it via this.config.initialVariants[key]
          // without null-checking.
          //
          // Passing undefined overwrites the default and causes: "Cannot read properties of undefined"
          // Using conditional spread omits the key entirely, preserving the SDK's default {}
          ...(ssrVariants && { initialVariants: ssrVariants }),
        };
        return initializeExperimentClient(config.amplitudeExperimentApiKey, sdkConfig);
      });

      return client;
    } catch (error) {
      // The SDK constructor reads sessionStorage, which throws SecurityError when the browser
      // blocks storage (Safari "Block all cookies", opaque-origin iframes). This memo runs during
      // render, so an uncaught throw reaches AppErrorBoundary and white-screens the entire app.
      logEvent('InitializeExperimentClient', { status: 'error', error: parseError(error) });
      return null;
    }
    // `isServer` is no longer a dependency: which SDK this memo constructs is now decided at build
    // time by module resolution rather than at runtime by the flag.
  }, [ssrVariants]);

  const isFirstSdkLoad = useRef(true);

  const amplitudeExperimentService = useMemo<RemoteFeatureFlagService>(() => {
    /**
     * Server SDK vs Client SDK API Differences:
     *
     * Server SDK (@amplitude/experiment-node-server):
     * - RemoteEvaluationClient.fetch(user): Promise<Variants>
     * - Returns Variants object directly, no internal state
     *
     * Client SDK (@amplitude/experiment-js-client):
     * - ExperimentClient has .start(), .fetch(), .variant(), .exposure()
     * - Maintains internal state with variants
     *
     * During SSR:
     * - fetchUser is never called (useEffect doesn't run - dependencies not ready)
     * - Only getFeatureValue is called during rendering
     * - Variants come from serverVariantsRef (populated via initialVariantsProp)
     */
    if (isServer) {
      return {
        fetchUser: async () => {
          // No-op: Never called during SSR (useEffect doesn't run)
        },
        getFeatureValue: (key: string) => {
          const variant = serverVariantsRef.current[key.toLowerCase()];
          return variant || {};
        },
        trackExposure: () => {
          // No-op: Exposure tracking calls happen during SSR, but we skip server-side tracking
          // because the same exposures will be tracked again on the client during hydration.
          // This prevents duplicate exposure events for SSR-rendered components.
        },
      };
    }

    // The SDK could not be constructed (storage-blocked browser). Degrade to the SSR variants
    // so flags resolve to the values the page was rendered with, rather than crashing.
    if (!experimentSdk) {
      return {
        fetchUser: async () => {
          // No-op: there is no SDK instance to fetch into.
        },
        getFeatureValue: (key: string) => {
          const variant = ssrVariants?.[key.toLowerCase()];
          return variant || {};
        },
        trackExposure: () => {
          // No-op: no SDK instance to record exposures against.
        },
      };
    }

    // Client SDK: Use full API
    return {
      fetchUser: async (experimentUser: ExperimentUser) => {
        let loadPromise: Promise<unknown>;

        if (isFirstSdkLoad.current) {
          isFirstSdkLoad.current = false;
          loadPromise = experimentSdk.start.call(experimentSdk, experimentUser);
        } else {
          loadPromise = experimentSdk.fetch.call(experimentSdk, experimentUser);
        }

        await loadPromise;
      },
      getFeatureValue: (key: string) => {
        try {
          return experimentSdk.variant(key.toLowerCase());
        } catch {
          return { value: undefined };
        }
      },
      trackExposure: (key: string) => {
        try {
          experimentSdk.exposure(key.toLowerCase());
        } catch {
          // Silently ignore exposure tracking failures
        }
      },
    };
  }, [experimentSdk, isServer, ssrVariants]);

  return amplitudeExperimentService;
}
